import React, { useCallback, useEffect, useState } from 'react';

// Groups the manufacturer-mismatch queue by (AHRI brand -> candidate
// manufacturer). Each row is clickable to review the records, bulk-approve the
// whole group, and add the brand alias for future runs.
export default function MismatchGroups({ api, onReview, onApprove, onError, onChanged }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maxAlignment, setMaxAlignment] = useState(0.4);
  const [busyKey, setBusyKey] = useState('');
  const [expanded, setExpanded] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.mismatchGroups(maxAlignment);
      setGroups(data.groups || []);
    } catch (e) {
      onError && onError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [api, maxAlignment, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const addAlias = useCallback(
    async (group) => {
      setBusyKey(`${group.ahri_brand}:${group.candidate_manufacturer}:alias`);
      try {
        await api.createAlias({
          canonical_name: group.candidate_manufacturer,
          alias_name: group.ahri_brand,
        });
        onError && onError(
          `Alias added: ${group.ahri_brand} → ${group.candidate_manufacturer}. Takes effect on the next mapping run.`
        );
        await load();
        onChanged && onChanged();
      } catch (e) {
        onError && onError(e.message || String(e));
      } finally {
        setBusyKey('');
      }
    },
    [api, load, onError, onChanged]
  );

  const key = (g) => `${g.ahri_brand} → ${g.candidate_manufacturer}`;

  return (
    <div className="card mb-3">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <span className="fw-semibold">Manufacturer mismatch groups</span>
        <div className="d-flex align-items-center gap-2">
          <label className="small text-muted mb-0">Max alignment</label>
          <input
            type="number" min="0" max="1" step="0.05"
            className="form-control form-control-sm" style={{ width: 90 }}
            value={maxAlignment}
            onChange={(e) => setMaxAlignment(Number(e.target.value))}
          />
          <button className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>
      <div className="card-body">
        <p className="small text-muted mb-2">
          Brand pairs flagged as a manufacturer mismatch (often the same maker spelled
          differently). Review a group, bulk-approve it, or add an alias so future mapping
          runs treat the brands as equivalent.
        </p>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>AHRI brand</th>
                <th>Candidate manufacturer</th>
                <th className="text-end">Entries</th>
                <th className="text-end">Rows</th>
                <th className="text-end">Avg score</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 && !loading && (
                <tr><td colSpan="6" className="text-center text-muted">No manufacturer mismatches at this threshold.</td></tr>
              )}
              {groups.map((g) => (
                <React.Fragment key={key(g)}>
                  <tr>
                    <td className="fw-semibold">{g.ahri_brand}</td>
                    <td>
                      {g.candidate_manufacturer}
                      {g.alias_exists && (
                        <span className="badge text-bg-success ms-2" title="An alias already covers this brand">
                          alias set
                        </span>
                      )}
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-link btn-sm p-0"
                        title="Show sample records"
                        onClick={() => setExpanded(expanded === key(g) ? '' : key(g))}
                      >
                        {g.entries}
                      </button>
                    </td>
                    <td className="text-end">{g.rows}</td>
                    <td className="text-end">{g.avg_score}</td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-secondary" onClick={() => onReview(g)} title="Filter the queue to this group">
                          Review
                        </button>
                        <button className="btn btn-primary" onClick={() => onApprove(g)} title="Bulk-approve this group now">
                          Approve all
                        </button>
                        {!g.alias_exists && (
                          <button
                            className="btn btn-outline-success"
                            onClick={() => addAlias(g)}
                            disabled={busyKey === `${g.ahri_brand}:${g.candidate_manufacturer}:alias`}
                            title="Add alias so future runs treat these brands as equivalent"
                          >
                            + Alias
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expanded === key(g) && (
                    <tr>
                      <td colSpan="6" className="bg-light">
                        <div className="small fw-semibold mb-1">
                          Sample records · fields: {g.by_field.map((f) => `${f.field} (${f.count})`).join(', ')}
                        </div>
                        <table className="table table-sm mb-0">
                          <thead>
                            <tr><th>AHRI</th><th>Field</th><th>Source</th><th>Candidate</th><th>MFR PN</th><th>Score</th></tr>
                          </thead>
                          <tbody>
                            {g.samples.map((s, i) => (
                              <tr key={`${s.ahri_id}-${i}`}>
                                <td>{s.ahri_id}</td>
                                <td>{s.field}</td>
                                <td><code>{s.source_value}</code></td>
                                <td>{s.product_code}</td>
                                <td>{s.manufacturer_part_number}</td>
                                <td>{Number(s.score || 0).toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
